import React from "react"
import { Container, Typography, Box, Paper, Link } from "@mui/material"

function TermsPage() {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ color: "primary.main", borderBottom: 2, borderColor: "divider", pb: 1 }}>
          Terms of Service
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 4 }}>
          Last Updated: July 2026
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Agreement to Terms
        </Typography>
        <Typography paragraph>
          By downloading, installing, accessing, or using the TNKL mobile application (the "App"), you agree to be bound by these Terms of Service
          ("Terms"). If you do not agree to these Terms, do not use the App.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Description of Service
        </Typography>
        <Typography paragraph>TNKL is a mobile application that allows users to:</Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Find and discover public bathroom locations</li>
          <li>Add and share bathroom listings with the community</li>
          <li>Rate and review bathrooms</li>
          <li>Communicate with other users</li>
          <li>Earn points and achievements for contributions</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Eligibility
        </Typography>
        <Typography paragraph>You must be at least 13 years old to use the App. By using the App, you represent and warrant that:</Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>You are at least 13 years old</li>
          <li>You have the legal capacity to enter into these Terms</li>
          <li>You will comply with all applicable laws and regulations</li>
          <li>All information you provide is accurate and truthful</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Account Registration
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Account Creation
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>You must create an account to use certain features of the App</li>
          <li>You must provide accurate, current, and complete information</li>
          <li>You are responsible for maintaining the confidentiality of your account credentials</li>
          <li>You are responsible for all activities that occur under your account</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Account Security
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>You must immediately notify us of any unauthorized use of your account</li>
          <li>We are not liable for any loss or damage arising from unauthorized account access</li>
          <li>You may not share your account credentials with others</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          User Conduct and Content
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Acceptable Use
        </Typography>
        <Typography paragraph>You agree to use the App only for lawful purposes and in accordance with these Terms. You agree NOT to:</Typography>

        <Typography variant="subtitle1" component="h4" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
          1. Post Inappropriate Content
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Upload offensive, obscene, pornographic, or sexually explicit content</li>
          <li>Post content that violates any laws or regulations</li>
          <li>Upload content that infringes on intellectual property rights</li>
          <li>Post false, misleading, or fraudulent information</li>
        </Box>

        <Typography variant="subtitle1" component="h4" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
          2. Harm Others
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Harass, threaten, or abuse other users</li>
          <li>Post content that discriminates against any individual or group</li>
          <li>Impersonate others or provide false information</li>
          <li>Stalk or invade the privacy of other users</li>
        </Box>

        <Typography variant="subtitle1" component="h4" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
          3. Misuse the Service
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Spam, flood, or post repetitive content</li>
          <li>Use automated systems to access the App</li>
          <li>Attempt to gain unauthorized access to the App or other users' accounts</li>
          <li>Interfere with or disrupt the App's functionality</li>
          <li>Use the App for commercial purposes without permission</li>
        </Box>

        <Typography variant="subtitle1" component="h4" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
          4. Violate Privacy
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Post personal information about others without consent</li>
          <li>Share private conversations without permission</li>
          <li>Use location data to track or harm others</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Content Moderation
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>We reserve the right to review, edit, or remove any content at any time</li>
          <li>We may suspend or terminate accounts that violate these Terms</li>
          <li>Users can report inappropriate content through the flagging system</li>
          <li>We are not obligated to monitor all content but may do so at our discretion</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Zero Tolerance Policy
        </Typography>
        <Typography paragraph>TNKL has ZERO TOLERANCE for objectionable content or abusive users.</Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>
            Any user who posts objectionable, offensive, or abusive content will have their content immediately removed and their account permanently
            banned.
          </li>
          <li>All reports of objectionable content or abusive behavior are reviewed and acted upon within 24 hours.</li>
          <li>We actively monitor and moderate all user-generated content.</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Location Services
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Location Data
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>The App requires location permissions to provide core functionality</li>
          <li>You can disable location services in your device settings</li>
          <li>
            We use location data to:
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Show nearby bathrooms</li>
              <li>Provide location-based notifications</li>
              <li>Help you add accurate bathroom locations</li>
            </Box>
          </li>
          <li>Location data is shared with other users only when you add a bathroom listing</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Location Accuracy
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>We strive for accuracy but cannot guarantee the accuracy of all location data</li>
          <li>User-submitted locations may contain errors</li>
          <li>Always verify bathroom locations before visiting</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Intellectual Property
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Our Rights
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>The App, including its design, features, and content, is owned by us or our licensors</li>
          <li>All trademarks, logos, and brand names are our property</li>
          <li>You may not copy, modify, or distribute the App without permission</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          User Content and Ownership
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>All content you submit (including bathroom locations, reviews, photos, and ratings) becomes the property of TNKL</li>
          <li>
            By posting content, you transfer all ownership rights to us and grant us unlimited rights to use, modify, and distribute your content
          </li>
          <li>You represent that you have the right to post all content you submit</li>
          <li>You may not post content that infringes on others' intellectual property rights</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Points, Achievements, and Gamification
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Points and achievements are virtual rewards with no monetary value</li>
          <li>Points cannot be transferred, sold, or exchanged</li>
          <li>We reserve the right to modify, suspend, or terminate the gamification system</li>
          <li>Points and achievements may be reset or adjusted at our discretion</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Privacy
        </Typography>
        <Typography paragraph>
          Your use of the App is also governed by our Privacy Policy. Please review our Privacy Policy to understand how we collect, use, and protect
          your information.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Disclaimers and Limitations
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          No Warranty
        </Typography>
        <Typography paragraph>
          THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES,
          INCLUDING:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>MERCHANTABILITY</li>
          <li>FITNESS FOR A PARTICULAR PURPOSE</li>
          <li>NON-INFRINGEMENT</li>
          <li>ACCURACY, RELIABILITY, OR COMPLETENESS OF ANY INFORMATION</li>
          <li>AVAILABILITY, ACCESSIBILITY, OR CONDITION OF BATHROOMS</li>
          <li>UNINTERRUPTED OR ERROR-FREE OPERATION OF THE APP</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          No Responsibility for Accuracy
        </Typography>
        <Typography paragraph sx={{ fontWeight: 600 }}>
          WE ARE NOT RESPONSIBLE FOR THE ACCURACY, COMPLETENESS, RELIABILITY, OR AVAILABILITY OF ANY INFORMATION PROVIDED THROUGH THE APP.
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>All bathroom listings, locations, and information are user-generated and may be inaccurate, outdated, or incomplete</li>
          <li>We do not verify, validate, or guarantee the accuracy of any user-submitted content</li>
          <li>We are not responsible for the condition, cleanliness, accessibility, safety, or availability of any listed bathroom</li>
          <li>Bathroom hours, accessibility features, and amenities may change without notice</li>
          <li>You use all information at your own risk and should independently verify any information before relying on it</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          User-Generated Content Disclaimer
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>We do not endorse, verify, or take responsibility for any user-generated content</li>
          <li>Reviews, ratings, photos, and descriptions are opinions of individual users and do not reflect our views</li>
          <li>We are not responsible for the accuracy, truthfulness, or legality of user-submitted content</li>
          <li>Users are solely responsible for their own content and interactions</li>
          <li>Use your own judgment when relying on user-submitted information</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          No Liability for Third-Party Locations
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>We do not own, operate, or control any of the bathrooms or locations listed in the App</li>
          <li>We are not responsible for any issues, injuries, damages, or losses that occur at any listed location</li>
          <li>We are not liable for the actions or policies of property owners, businesses, or other third parties</li>
          <li>You access all locations at your own risk</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          No Professional Advice
        </Typography>
        <Typography paragraph>
          The App does not provide medical, health, safety, or professional advice. Information provided is for general informational purposes only.
          Consult appropriate professionals for specific advice.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Limitation of Liability
        </Typography>
        <Typography paragraph sx={{ fontWeight: 600 }}>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, TNKL, ITS OWNERS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY DAMAGES WHATSOEVER
          ARISING FROM YOUR USE OF THE APP.
        </Typography>
        <Typography paragraph>THIS INCLUDES, BUT IS NOT LIMITED TO:</Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Any direct, indirect, incidental, special, consequential, or punitive damages</li>
          <li>Personal injury, illness, death, or property damage of any kind</li>
          <li>Loss of data, profits, revenue, or business opportunities</li>
          <li>Emotional distress or psychological harm</li>
          <li>Inaccuracies, errors, or omissions in location data, bathroom information, or any other content</li>
          <li>Actions, content, or conduct of other users or third parties</li>
          <li>Unauthorized access to or use of your account or data</li>
          <li>Interruption or cessation of the App or any features</li>
          <li>Bugs, viruses, or other harmful components</li>
          <li>Any issues arising from visiting or using any bathroom or location listed in the App</li>
          <li>Reliance on any information provided through the App</li>
        </Box>
        <Typography paragraph sx={{ fontWeight: 600, mt: 2 }}>
          IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU EXCEED THE AMOUNT YOU PAID TO US IN THE PAST 12 MONTHS.
        </Typography>
        <Typography paragraph>
          SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES OR LIMITATION OF LIABILITY, SO SOME OF THE ABOVE LIMITATIONS MAY NOT
          APPLY TO YOU.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Assumption of Risk
        </Typography>
        <Typography paragraph sx={{ fontWeight: 600 }}>
          YOU ACKNOWLEDGE AND AGREE THAT YOU USE THE APP AND VISIT ANY LISTED LOCATIONS ENTIRELY AT YOUR OWN RISK.
        </Typography>
        <Typography paragraph>By using the App, you acknowledge and accept the following risks:</Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Bathroom locations may be inaccurate, inaccessible, unsafe, or unavailable</li>
          <li>Bathrooms may be in poor condition, unsanitary, or pose health and safety risks</li>
          <li>You may encounter dangerous conditions, hazards, or individuals at listed locations</li>
          <li>Information provided by other users may be false, misleading, or harmful</li>
          <li>The App may malfunction, provide incorrect information, or be unavailable when needed</li>
          <li>You are solely responsible for your own safety and well-being when using the App</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Indemnification
        </Typography>
        <Typography paragraph>
          You agree to indemnify, defend, and hold harmless TNKL, its owners, employees, agents, and affiliates from any claims, damages, losses,
          liabilities, and expenses (including reasonable legal fees) arising from:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Your use of the App</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any laws or regulations</li>
          <li>Your violation of any rights of others</li>
          <li>Content you post or submit</li>
          <li>Your interactions with other users</li>
          <li>Any injury, damage, or loss you experience while using the App or visiting any listed location</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Termination
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          By You
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>You may stop using the App at any time</li>
          <li>You may delete your account in the App Settings or by contacting us at support@crappermapper.com</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          By Us
        </Typography>
        <Typography paragraph>We may suspend or terminate your access to the App if you:</Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Violate these Terms</li>
          <li>Engage in fraudulent or illegal activity</li>
          <li>Abuse or harm other users</li>
          <li>Post inappropriate content</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Effect of Termination
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Upon termination, your right to use the App will immediately cease</li>
          <li>We may delete your account and associated data</li>
          <li>Some information may be retained as required by law</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Changes to Terms
        </Typography>
        <Typography paragraph>We may modify these Terms at any time. We will notify you of material changes by:</Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Posting updated Terms in the App</li>
          <li>Updating the "Last Updated" date</li>
          <li>Sending you a notification (for material changes)</li>
        </Box>
        <Typography paragraph>Your continued use of the App after changes become effective constitutes acceptance of the updated Terms.</Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Dispute Resolution
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Governing Law
        </Typography>
        <Typography paragraph>These Terms are governed by the laws of the United States, without regard to conflict of law principles.</Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Dispute Resolution Process
        </Typography>
        <Box component="ol" sx={{ pl: 3 }}>
          <li>
            <strong>Informal Resolution</strong>: Contact us first to attempt to resolve any dispute
          </li>
          <li>
            <strong>Binding Arbitration</strong>: If informal resolution fails, disputes will be resolved through binding arbitration
          </li>
          <li>
            <strong>Class Action Waiver</strong>: You waive the right to participate in class action lawsuits
          </li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Miscellaneous
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Entire Agreement
        </Typography>
        <Typography paragraph>These Terms, together with our Privacy Policy, constitute the entire agreement between you and us.</Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Severability
        </Typography>
        <Typography paragraph>
          If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full effect.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Assignment
        </Typography>
        <Typography paragraph>
          You may not assign or transfer these Terms or your account without our consent. We may assign these Terms at any time.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Contact Information
        </Typography>
        <Typography paragraph>If you have questions about these Terms, please contact us at:</Typography>
        <Typography paragraph>
          <strong>Email</strong>: <Link href="mailto:support@crappermapper.com">support@crappermapper.com</Link>
          <br />
          <strong>Website</strong>: <Link href="https://tnkl.app">https://tnkl.app</Link>
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Acknowledgment
        </Typography>
        <Typography paragraph sx={{ fontWeight: 600 }}>
          BY USING THE APP, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.
        </Typography>
      </Paper>
    </Container>
  )
}

export default TermsPage
